import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo,
} from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CartTable from "../cart-table";
import {
  Header,
  StyledTitle,
  StyledMoneyText,
  TableContainer,
  FlexContainer,
  SummaryContainer,
  LeftContainer,
  DetailContainer,
  DetailText,
  PinContainer,
  StyledTextField,
  StyledButton,
  StyledClearIcon,
  TooltipTitle,
  ReasonContainer,
  NumberText,
  CircularProgressContainer,
  StyledCircularProgress,
  CashCardSelectorContainer,
  AmountsContainer,
} from "./confirmation-modal.styled";
import { postHttpResponse } from "@/api/helper";
import HtmlTooltip from "@/components/htmlTooltip";
import InfoContainer from "@/components/Info-container";
import CashCardSelector from "@/components/cash-card-selector";
import { paymentMethods, accountTypes } from "@/constants";
import PaymentAmountContainer from "@/components/payment-amount-container";
import amountToFixed from "@/helper/amountToFixed";
import { AppContext } from "@/contexts/AppContext";
import PrintReport from "@/components/PrintReport";
import reducer, { initialState, actionTypes } from "./reducer";
import { useMainPageContext } from "@/contexts/MainPageContext";
import useGetUserInfoQuery from "@/hooks/queries/useGetUserInfoQuery";
import { benefitsMapping } from "@/api/constants";
import { validatePosTender } from "@/helper/posCartEngine";

const style = (isFullSize) => {
  return {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isFullSize ? "100%" : "91%",
    height: isFullSize ? "100%" : "85%",
    bgcolor: "background.paper",
    borderRadius: "4px",
    boxShadow: 24,
  };
};

export default function BasicModal({ open, setOpen, balance }) {
  const [showWrongPinAlert, setShowWrongPinAlert] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const {
    selectedPaymentMethod,
    setAlert,
    selectedAccountTypeId,
    clearSelectedEmployee,
    selectedProductsWithPromotions,
    usedGiftCards,
    checkoutAccepted,
    checkoutTotals,
    noPinCashCard,
    isPinCashCardReady
  } = useMainPageContext();
  const {
    userId,
    token,
    itbis,
    subTotal,
    discountAmount,
    companyBranchId,
    selectedEmployee: employee,
    applicableBenefits,
    toUseBenefits,
    amountToProcess,
    cashOrCardValue,
  } = useContext(AppContext);
  const { data: userInfo } = useGetUserInfoQuery();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    pinValue,
    openPinTooltip,
    openCashTooltip,
    openPaymentMethodTooltip,
    openReasonTooltip,
    orderReason,
    isLoading,
    cashOrCardMethod,
    cashValue,
    isPrintReport,
    receiptUrl,
  } = state;
  const isCardOrCashPayment = selectedPaymentMethod === paymentMethods.efectivo || selectedPaymentMethod === paymentMethods.tarjeta;
  const idempotencyKey = useRef();
  const pinEnabled = useMemo(()=>{
    return (applicableBenefits?.showPin ?? false) && !(noPinCashCard && isCardOrCashPayment);
  },[applicableBenefits, noPinCashCard, isCardOrCashPayment]);
  useEffect(() => {
    idempotencyKey.current = crypto.randomUUID();
  }, [open]); 
  const postOrder = async (body) => {
    dispatch({ type: actionTypes.setIsLoading, payload: true });
    try {
      const response = await postHttpResponse(
        "create-order-service/createposorder",
        body,
        token,
        userId,
        { "idempotency-key": idempotencyKey.current },
      );
      if (response?.data?.success) {
        if (response.data.showReceipt && response.data.downloadReceiptUrl) {
          const clearReceiptId = response.data.downloadReceiptUrl?.replace(
            /\D/g,
            "",
          );
          dispatch({
            type: actionTypes.setReceiptUrl,
            payload: clearReceiptId,
          });
          //setAlert({open: true, severity: 'success', message: 'Orden Realizada', duration: 3000})
          clearSelectedEmployee();
        } else {
          clearSelectedEmployee();
          handleClose();
          setAlert({
            open: true,
            severity: "success",
            message: "Orden Realizada",
            duration: 3000,
          });
        }
      }
    } catch (error) {
      if (error?.response?.data?.errorCode === "Error-005") {
        setShowWrongPinAlert(true);
      }
      setAlert({
        open: true,
        severity: "error",
        message:
          error?.response?.data?.errorMessage ||
          error?.message ||
          "Ocurrió un error buscando los datos",
        duration: 3000,
      });
    }
    dispatch({ type: actionTypes.setIsLoading, payload: false });
  };

  const handleReasonChange = (value) => {
    if (value.length <= 700) {
      dispatch({ type: actionTypes.setOrderReason, payload: value });
    }
  };

  const handleClose = () => {
    dispatch({ type: actionTypes.setPinValue, payload: "" });
    dispatch({ type: actionTypes.setOpenPinTooltip, payload: false });
    dispatch({ type: actionTypes.setOpenCashTooltip, payload: false });
    dispatch({ type: actionTypes.setOpenReasonTooltip, payload: false });
    dispatch({ type: actionTypes.setOrderReason, payload: "" });
    dispatch({ type: actionTypes.setCashOrCardMethod, payload: undefined });
    dispatch({ type: actionTypes.setCashValue, payload: "" });
    setOpen(false);
    dispatch({ type: actionTypes.setIsPrintReport, payload: false });
  };

  const handlePinChange = (value) => {
    const onlyNumbers = value.replace(/\D/g, "");
    if (onlyNumbers.length > 4) return;
    setShowWrongPinAlert(false);
    dispatch({ type: actionTypes.setPinValue, payload: onlyNumbers });
  };

  const handleOpenPinTooltip = () => {
    dispatch({ type: actionTypes.setOpenPinTooltip, payload: true });
    setTimeout(
      () => dispatch({ type: actionTypes.setOpenPinTooltip, payload: false }),
      3000,
    );
  };

  const handleOpenCashTooltip = () => {
    dispatch({ type: actionTypes.setOpenCashTooltip, payload: true });
    setTimeout(
      () => dispatch({ type: actionTypes.setOpenCashTooltip, payload: false }),
      3000,
    );
  };

  const handleOpenPaymentMethodTooltip = () => {
    dispatch({ type: actionTypes.setOpenPaymentMethodTooltip, payload: true });
    setTimeout(
      () =>
        dispatch({
          type: actionTypes.setOpenPaymentMethodTooltip,
          payload: false,
        }),
      3000,
    );
  };

  const handleOpenReasonTooltip = () => {
    dispatch({ type: actionTypes.setOpenReasonTooltip, payload: true });
    setTimeout(
      () =>
        dispatch({ type: actionTypes.setOpenReasonTooltip, payload: false }),
      3000,
    );
  };

  const tenderValidation = validatePosTender({
    amountToProcess,
    cashOrCardMethod,
    cashOrCardValue,
    cashValue,
    selectedPaymentMethod,
  });

  const handleConfirm = async () => {
    setIsConfirming(true);
    if (!checkoutAccepted) {
      setIsConfirming(false);
      return;
    }
    let validation = true;

    if (pinValue.length < 4 && pinEnabled) {
      handleOpenPinTooltip();
      validation = false;
    }

    if (tenderValidation.errors.includes("SECONDARY_TENDER_REQUIRED")) {
      handleOpenPaymentMethodTooltip();
      validation = false;
    }

    if (
      orderReason.length === 0 &&
      selectedAccountTypeId === accountTypes.company
    ) {
      handleOpenReasonTooltip();
      validation = false;
    }

    if (
      tenderValidation.errors.includes("CASH_AMOUNT_REQUIRED") ||
      tenderValidation.errors.includes("CASH_AMOUNT_INSUFFICIENT")
    ) {
      handleOpenCashTooltip();
      validation = false;
    }
    const giftCardIds =
      usedGiftCards.length > 0
        ? usedGiftCards.map((giftCard) => giftCard.id)
        : undefined;

    if (!validation) {
      setIsConfirming(false);
      return;
    }
    const orderProducts = selectedProductsWithPromotions.map((product) => {
      return {
        cashOrCardValue: Number(product.payment.externalAmount.toFixed(2)),
        catalogId: product.catalogId,
        combo: product.combo.toString(),
        deliveryDate: product.deliveryDate,
        deliveryTypeId: product.deliveryTypeId,
        itemId: product.id.toString(),
        promotionId: product.appliedPromotionId,
        orderReason,
        providerId: product.providerId,
        quantity: parseInt(product.quantity),
        sections: product.sections,
        subTotal: product.unitPrice,
        userType: product.userType,
        giftCardIds,
      };
    });
    const payload = {
      id: employee.id,
      nameUser: employee.name,
      paymentMethod: selectedPaymentMethod,
      pin: pinValue,
      products: orderProducts,
      selectedCompanyBranchId: companyBranchId,
      subPaymentMethod: cashOrCardMethod,
      vendorId: userInfo?.data?.vendorId,
      validatePin: pinEnabled,
    };
    await postOrder(payload);
    setIsConfirming(false);
  };

  return (
    <div>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style(isPrintReport)}>
          {isPrintReport && (
            <PrintReport
              setAlert={setAlert}
              receiptUrl={receiptUrl}
              handleClose={handleClose}
            />
          )}
          {!isPrintReport && (
            <FlexContainer>
              <LeftContainer>
                <Header
                  backgroundcolor={
                    selectedAccountTypeId === accountTypes.employee
                      ? "#6ec4e8"
                      : "#ff8d6a"
                  }
                >
                  <StyledTitle>{employee.name}</StyledTitle>
                  <StyledMoneyText>{balance}</StyledMoneyText>
                </Header>
                <TableContainer>
                  <CartTable
                    selectedProducts={selectedProductsWithPromotions}
                  />
                </TableContainer>
              </LeftContainer>
              <SummaryContainer>
                <StyledClearIcon onClick={handleClose} />
                <AmountsContainer>
                  {discountAmount > 0 && (
                    <>
                      <DetailContainer>
                        <DetailText>Subtotal Pre descuento</DetailText>
                        <DetailText>{`RD$ ${amountToFixed(checkoutTotals.grossSubtotal, 2)}`}</DetailText>
                      </DetailContainer>
                      <DetailContainer>
                        <DetailText textcolor="#6ec4e8">Promoción</DetailText>
                        <DetailText textcolor="#6ec4e8">{`RD$ ${amountToFixed(discountAmount, 2)}`}</DetailText>
                      </DetailContainer>
                    </>
                  )}
                  <DetailContainer>
                    <DetailText>Subtotal</DetailText>
                    <DetailText>{`RD$ ${amountToFixed(subTotal, 2)}`}</DetailText>
                  </DetailContainer>

                  {Object.entries(itbis).map((entry) => (
                    <DetailContainer key={entry[0]}>
                      <DetailText>{entry[0]}</DetailText>
                      <DetailText>{`RD$ ${amountToFixed(entry[1], 2)}`}</DetailText>
                    </DetailContainer>
                  ))}

                  {Object.entries(toUseBenefits)
                    .filter((entry) => entry[1] > 0)
                    .map((entry) => (
                      <DetailContainer key={entry[0]}>
                        <DetailText textcolor="#6ec4e8">
                          {benefitsMapping[entry[0]] ??
                            entry[0][0].toUpperCase() + entry[0].slice(1)}
                        </DetailText>
                        <DetailText textcolor="#6ec4e8">
                          {`RD$ ${amountToFixed(entry[1], 2)}`}
                        </DetailText>
                      </DetailContainer>
                    ))}

                  {checkoutTotals.roundingAdjustment ? (
                    <DetailContainer>
                      <DetailText>Ajuste de redondeo</DetailText>
                      <DetailText>{`RD$ ${amountToFixed(checkoutTotals.roundingAdjustment, 2)}`}</DetailText>
                    </DetailContainer>
                  ) : null}

                  <DetailContainer>
                    <DetailText bold>
                      {selectedPaymentMethod === paymentMethods.nomina &&
                        "Total"}
                      {selectedPaymentMethod === paymentMethods.efectivo &&
                        "Total Efectivo"}
                      {selectedPaymentMethod === paymentMethods.tarjeta &&
                        "Total Tarjeta"}
                    </DetailText>
                    <DetailText
                      bold
                    >{`RD$ ${amountToFixed(amountToProcess, 2)}`}</DetailText>
                  </DetailContainer>

                  {Number(cashOrCardValue.toFixed(2)) ? (
                    <InfoContainer
                      cashOrCardValue={Number(cashOrCardValue.toFixed(2))}
                    />
                  ) : null}
                </AmountsContainer>

                {(Number(cashOrCardValue.toFixed(2)) &&
                  selectedPaymentMethod === paymentMethods.nomina) ||
                selectedPaymentMethod === paymentMethods.efectivo ? (
                  <CashCardSelectorContainer>
                    {Number(cashOrCardValue.toFixed(2)) &&
                    selectedPaymentMethod === paymentMethods.nomina ? (
                      <CashCardSelector
                        primaryPaymentMethod={selectedPaymentMethod}
                        cashValue={cashValue}
                        setCashValue={(payload) =>
                          dispatch({ type: actionTypes.setCashValue, payload })
                        }
                        openPaymentMethodTooltip={openPaymentMethodTooltip}
                        openCashTooltip={openCashTooltip}
                        amount={Number(cashOrCardValue.toFixed(2))}
                        setSecondaryPaymentMethod={(payload) =>
                          dispatch({
                            type: actionTypes.setCashOrCardMethod,
                            payload,
                          })
                        }
                        selectedSecondaryPaymentMethod={cashOrCardMethod}
                        change={tenderValidation.change}
                      />
                    ) : Number(amountToProcess.toFixed(2)) ? (
                      <PaymentAmountContainer
                        amount={Number(amountToProcess.toFixed(2))}
                        primaryPaymentMethod={selectedPaymentMethod}
                        cashValue={cashValue}
                        setCashValue={(payload) =>
                          dispatch({ type: actionTypes.setCashValue, payload })
                        }
                        opentooltip={openCashTooltip}
                        change={tenderValidation.change}
                      />
                    ) : null}
                  </CashCardSelectorContainer>
                ) : null}

                <PinContainer margintop={openPaymentMethodTooltip && 60}>
                  {pinEnabled && (
                    <>
                      <DetailText bold>Digitar Pin</DetailText>
                      <HtmlTooltip
                        arrow
                        open={openPinTooltip}
                        placement="bottom-start"
                        title={<TooltipTitle>Digita el PIN</TooltipTitle>}
                      >
                        <StyledTextField
                          variant="outlined"
                          type="password"
                          value={pinValue}
                          onChange={(e) => handlePinChange(e.target.value)}
                          inputProps={{
                            style: { fontSize: 24 },
                          }}
                        />
                      </HtmlTooltip>
                      {showWrongPinAlert && (
                        <small style={{ color: "#f44336" }}>
                          Pin incorrecto
                        </small>
                      )}
                    </>
                  )}
                  {selectedAccountTypeId === accountTypes.company && (
                    <ReasonContainer opentooltip={openPinTooltip}>
                      <DetailText align="left" marginBottom={11}>
                        Razón de Orden*
                      </DetailText>
                      <HtmlTooltip
                        arrow
                        open={openReasonTooltip}
                        placement="bottom-start"
                        title={
                          <TooltipTitle>
                            Especifique la Razón de Orden
                          </TooltipTitle>
                        }
                      >
                        <StyledTextField
                          multiline
                          minRows={2}
                          maxRows={7}
                          variant="outlined"
                          value={orderReason}
                          onChange={(e) => handleReasonChange(e.target.value)}
                          inputProps={{
                            style: { fontSize: 18 },
                          }}
                        />
                      </HtmlTooltip>
                      <NumberText>{`${orderReason.length} / 700`}</NumberText>
                    </ReasonContainer>
                  )}
                  <StyledButton
                    opentooltip={
                      (openPinTooltip &&
                        selectedAccountTypeId === accountTypes.employee) ||
                      (openReasonTooltip &&
                        selectedAccountTypeId === accountTypes.company)
                    }
                    onClick={handleConfirm}
                    disabled={isConfirming || !isPinCashCardReady}
                    variant="contained"
                  >
                    {isLoading ? (
                      <CircularProgressContainer>
                        <StyledCircularProgress />
                      </CircularProgressContainer>
                    ) : (
                      "Confirmar"
                    )}
                  </StyledButton>
                </PinContainer>
              </SummaryContainer>
            </FlexContainer>
          )}
        </Box>
      </Modal>
    </div>
  );
}
